/**
 * 用量反応の計算過程 walkthrough（レガシー DoseResponseView GLM セクション相当・要約）
 */

import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { drMethodUsesApproximateInterval } from '../../../../utils/keisukeDoseResponseDefaults';
import type { DilutionData } from '../../../../utils/statistics/tcid50';
import type { Method } from '../../../../utils/statistics/doseResponse';
import { formatKeisukeNumber } from './keisukeDashboardStatLabels';

interface DoseResponseCalculationDetailsProps {
  rawResult: unknown;
  compact?: boolean;
}

export default function DoseResponseCalculationDetails({
  rawResult,
  compact = false,
}: DoseResponseCalculationDetailsProps) {
  const { t } = useTranslation();
  const Title = compact ? 'h6' : 'h5';

  if (!rawResult || typeof rawResult !== 'object') return null;
  const r = rawResult as Record<string, unknown>;
  const method = String(r.method ?? '');
  const doses = r.doses as number[] | undefined;
  const responses = r.responses as number[] | undefined;
  const dilutionData = r.dilutionData as DilutionData[] | undefined;
  const probitResult = r.probitResult as
    | { intercept: number; slope: number; isGLM?: boolean }
    | undefined;
  const logisticResult = r.logisticResult as
    | { intercept: number; slope: number; isGLM?: boolean }
    | undefined;
  const ci = r.confidenceInterval as { lower?: number; upper?: number } | undefined;
  const usesApprox =
    dilutionData != null ||
    drMethodUsesApproximateInterval(method as Method);

  return (
    <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
      <Title className="mb-2">{t('keisuke.doseResponse.calculationProcess')}</Title>

      {dilutionData && dilutionData.length > 0 ? (
        <div className="mb-3">
          <h6>{t('keisuke.doseResponse.detail.tcid50InputTable')}</h6>
          <Table striped bordered size="sm" className="mb-2">
            <thead>
              <tr>
                <th>{t('keisuke.doseResponse.detail.tcid50Col.dilution')}</th>
                <th>{t('keisuke.doseResponse.detail.tcid50Col.infected')}</th>
                <th>{t('keisuke.doseResponse.detail.tcid50Col.total')}</th>
                <th>{t('keisuke.doseResponse.detail.tcid50Col.rate')}</th>
              </tr>
            </thead>
            <tbody>
              {dilutionData.map((d, i) => (
                <tr key={i}>
                  <td>{formatKeisukeNumber(d.dilution, 4)}</td>
                  <td>{d.infected}</td>
                  <td>{d.total}</td>
                  <td>{formatKeisukeNumber(d.infectionRate * 100, 2)}%</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="text-muted mb-0">{t('keisuke.doseResponse.detail.tcid50Walkthrough')}</p>
        </div>
      ) : null}

      {(method === 'spearman-karber' || method === 'reed-muench') && doses && responses ? (
        <pre className="bg-light p-3 rounded mb-3 small">
          {`${method === 'reed-muench' ? t('keisuke.doseResponse.reedMuench') : t('keisuke.doseResponse.spearmanKarber')}\n` +
            `log₁₀ 用量: ${doses.map((d) => formatKeisukeNumber(d, 4)).join(', ')}\n` +
            `${t('keisuke.doseResponse.columnNames.responseRate')}: ${responses.map((p) => formatKeisukeNumber(p * 100, 2)).join(', ')}%`}
        </pre>
      ) : null}

      {(method === 'probit' || method === 'logistic') && (probitResult?.isGLM || logisticResult?.isGLM) ? (
        <div className="mb-3">
          <h6>{t('keisuke.doseResponse.glmMethod')}</h6>
          <p className="text-muted mb-2">{t('keisuke.doseResponse.glmDescription')}</p>
          <div className="bg-light p-3 rounded mb-2 font-monospace">
            {method === 'probit' ? (
              <>
                qnorm(p) = β₀ + β₁ × log₁₀(dose)
                <br />p = Φ(β₀ + β₁ × log₁₀(dose))
              </>
            ) : (
              <>logit(p) = β₀ + β₁ × log₁₀(dose)</>
            )}
          </div>
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>{t('keisuke.doseResponse.detail.glm.table.parameter')}</th>
                <th>{t('keisuke.doseResponse.detail.glm.table.value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('keisuke.doseResponse.detail.glm.table.beta0Label')}</td>
                <td>
                  {formatKeisukeNumber(
                    method === 'probit'
                      ? (probitResult?.intercept ?? NaN)
                      : (logisticResult?.intercept ?? NaN)
                  )}
                </td>
              </tr>
              <tr>
                <td>{t('keisuke.doseResponse.detail.glm.table.beta1Label')}</td>
                <td>
                  {formatKeisukeNumber(
                    method === 'probit' ? (probitResult?.slope ?? NaN) : (logisticResult?.slope ?? NaN)
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      ) : null}

      {r.value != null ? (
        <Table striped bordered size="sm" className="mb-0">
          <tbody>
            <tr>
              <td>{t('keisuke.doseResponse.detail.estimatedValue')}</td>
              <td>{Number(r.value).toExponential(4)}</td>
            </tr>
            {r.valueLog10 != null && Number.isFinite(Number(r.valueLog10)) ? (
              <tr>
                <td>{t('keisuke.doseResponse.detail.log10Value')}</td>
                <td>{Number(r.valueLog10).toFixed(4)}</td>
              </tr>
            ) : null}
            {ci ? (
              <>
                <tr>
                  <td>
                    {usesApprox
                      ? t('keisuke.doseResponse.detail.approxIntervalLower')
                      : t('keisuke.doseResponse.detail.ciLower')}
                  </td>
                  <td>{ci.lower?.toExponential(4) ?? '—'}</td>
                </tr>
                <tr>
                  <td>
                    {usesApprox
                      ? t('keisuke.doseResponse.detail.approxIntervalUpper')
                      : t('keisuke.doseResponse.detail.ciUpper')}
                  </td>
                  <td>{ci.upper?.toExponential(4) ?? '—'}</td>
                </tr>
              </>
            ) : null}
          </tbody>
        </Table>
      ) : null}
    </div>
  );
}
